// Signage → Campañas (/signage/campanas). Capas estacionales que se activan
// solas por fecha y se montan sobre la plantilla/tema. Presets editables +
// ámbito empresa/grupo/pantalla, con vista previa en vivo del efecto.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls } from '@/features/pos/shared';
import { MOMENTOS, sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';
import type { RefRow } from './shared';
import { SignagePlayer, playerCss } from './player';
import type { Categoria } from './player';
import { CAMPANAS, EFECTO_ACENTO } from './CampaignOverlay';

interface Campana {
  id: string; codigo: string | null; nombre: string; efecto: string;
  fecha_inicio: string; fecha_fin: string; recurrencia_anual: boolean;
  ambito: 'empresa' | 'grupo' | 'pantalla'; grupo_id: string | null; pantalla_id: string | null;
  acento_override: string | null; saludo: string | null; intensidad: number; prioridad: number; activa: boolean;
  momento: string | null;   // 420: null=siempre | quincena | fin_mes | pago
}
const EFECTOS = CAMPANAS.filter((c) => c.v);
const efLabel = (v: string) => EFECTOS.find((e) => e.v === v)?.l ?? v;
const mmdd = (d: string) => (d || '').slice(5, 10);
const SAMPLE: Categoria[] = [{ categoria: 'Destacados', n: 2, productos: [
  { id: 's1', nombre: 'Producto estrella', descripcion: null, marca: 'Marca', precio: 12.9, iva_tarifa: 15, controla_stock: true, stock: 3, agotado: false, imagen: null },
  { id: 's2', nombre: 'Otro producto', descripcion: null, marca: null, precio: 8.5, iva_tarifa: 15, controla_stock: false, stock: null, agotado: false, imagen: null },
] }];
const TEMA_CALIDO = { preset: 'calido', tokens: { paper: '#efe7da', paper2: '#e7ddcc', ink: '#191310', soft: '#6f655a', accent: '#c6303a', sage: '#4f7a52', oxido: '#b0552a', topo: '#8a8175' } };

export function Campanas() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Campana> | null>(null);
  const [guardando, setGuardando] = useState(false);

  const { data: campanas = [] } = useQuery<Campana[]>({ queryKey: ['sig-campanas'], queryFn: () => sigList<Campana>('campana?select=*&deleted_at=is.null&order=fecha_inicio') });
  const { data: grupos = [] } = useQuery<RefRow[]>({ queryKey: ['sig-grupos'], queryFn: () => sigList<RefRow>('grupo?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: pantallas = [] } = useQuery<RefRow[]>({ queryKey: ['sig-pantallas-ref'], queryFn: () => sigList<RefRow>('pantalla?select=id,nombre&deleted_at=is.null&order=nombre') });
  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-campanas'] });

  const toggleActiva = async (c: Campana) => { await sigUpdate('campana', c.id, { activa: !c.activa }); invalidar(); };

  const guardar = async () => {
    if (!edit?.nombre || !edit.efecto) { toast.error('Nombre y efecto son obligatorios'); return; }
    setGuardando(true);
    try {
      const body = {
        nombre: edit.nombre, efecto: edit.efecto, codigo: edit.codigo || null,
        fecha_inicio: edit.fecha_inicio, fecha_fin: edit.fecha_fin,
        recurrencia_anual: edit.recurrencia_anual ?? true,
        ambito: edit.ambito ?? 'empresa',
        grupo_id: edit.ambito === 'grupo' ? edit.grupo_id || null : null,
        pantalla_id: edit.ambito === 'pantalla' ? edit.pantalla_id || null : null,
        acento_override: edit.acento_override || null, saludo: edit.saludo || null,
        intensidad: edit.intensidad ?? 2, prioridad: edit.prioridad ?? 10, activa: edit.activa ?? true,
        momento: edit.momento || null,
      };
      if (edit.id) await sigUpdate('campana', edit.id, body); else await sigInsert('campana', body);
      setEdit(null); invalidar(); toast.success('Campaña guardada');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const previewData = useMemo(() => ({ empresa: 'Vista previa', local: edLabelLocal(edit), tema: TEMA_CALIDO, escenas: [{ tipo: 'catalogo', dur: 6 }], categorias: SAMPLE }), [edit]);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <style>{playerCss}</style>
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5" /> Campañas estacionales</h1>
        <Button className="ml-auto" onClick={() => setEdit({ efecto: 'navidad', fecha_inicio: '2024-12-01', fecha_fin: '2024-12-26', recurrencia_anual: true, ambito: 'empresa', intensidad: 2, activa: true })}>
          <Plus className="w-4 h-4 mr-1" /> Nueva campaña
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">Se activan solas cuando llega su fecha y se montan sobre lo que la pantalla esté mostrando (el catálogo sigue vendiendo). El ámbito permite segmentar por grupo o pantalla (p. ej. fiestas locales o regreso a clases por región).</p>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Campaña</th><th className="py-2 px-3 text-left">Efecto</th>
            <th className="py-2 px-3 text-left">Fechas</th><th className="py-2 px-3 text-left">Ámbito</th><th className="py-2 px-3 text-center">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {campanas.map((c, i) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{c.nombre}</td>
                <td className="py-2 px-3 text-muted-foreground">{efLabel(c.efecto)}</td>
                <td className="py-2 px-3 font-mono">{c.recurrencia_anual ? `${mmdd(c.fecha_inicio)} → ${mmdd(c.fecha_fin)} · anual` : `${c.fecha_inicio} → ${c.fecha_fin}`}</td>
                <td className="py-2 px-3">{c.ambito === 'grupo' ? `Grupo` : c.ambito === 'pantalla' ? 'Pantalla' : 'Toda la empresa'}</td>
                <td className="py-2 px-3 text-center"><button onClick={() => void toggleActiva(c)} className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.activa ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-muted text-muted-foreground'}`}>{c.activa ? 'Activa' : 'Inactiva'}</button></td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(c)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('campana', c.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!campanas.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin campañas.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <PanelIntegrado title={edit.id ? 'Editar campaña' : 'Nueva campaña'} onClose={() => setEdit(null)} wide>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
                  <Campo label="Efecto">
                    <select className={fieldCls} value={edit.efecto ?? ''} onChange={(e) => setEdit({ ...edit, efecto: e.target.value })}>
                      {EFECTOS.map((ef) => <option key={ef.v} value={ef.v}>{ef.l}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Desde (día/mes)"><input className={fieldCls} type="date" value={edit.fecha_inicio ?? ''} onChange={(e) => setEdit({ ...edit, fecha_inicio: e.target.value })} /></Campo>
                  <Campo label="Hasta (día/mes)"><input className={fieldCls} type="date" value={edit.fecha_fin ?? ''} onChange={(e) => setEdit({ ...edit, fecha_fin: e.target.value })} /></Campo>
                </div>
                <Switch checked={edit.recurrencia_anual ?? true} onChange={(v) => setEdit({ ...edit, recurrencia_anual: v })} label="Se repite cada año (usa solo día/mes)" />
                <Campo label="Momento (recurrente cada mes — con rango amplio, la campaña vive SOLO esos días)">
                  <select className={fieldCls} value={edit.momento ?? ''} onChange={(e) => setEdit({ ...edit, momento: e.target.value || null })}>
                    {MOMENTOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </Campo>
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Ámbito">
                    <select className={fieldCls} value={edit.ambito ?? 'empresa'} onChange={(e) => setEdit({ ...edit, ambito: e.target.value as Campana['ambito'] })}>
                      <option value="empresa">Toda la empresa</option><option value="grupo">Un grupo</option><option value="pantalla">Una pantalla</option>
                    </select>
                  </Campo>
                  {edit.ambito === 'grupo' && <Campo label="Grupo"><select className={fieldCls} value={edit.grupo_id ?? ''} onChange={(e) => setEdit({ ...edit, grupo_id: e.target.value })}><option value="">—</option>{grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}</select></Campo>}
                  {edit.ambito === 'pantalla' && <Campo label="Pantalla"><select className={fieldCls} value={edit.pantalla_id ?? ''} onChange={(e) => setEdit({ ...edit, pantalla_id: e.target.value })}><option value="">—</option>{pantallas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></Campo>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Saludo (opcional)"><input className={fieldCls} value={edit.saludo ?? ''} onChange={(e) => setEdit({ ...edit, saludo: e.target.value })} /></Campo>
                  <Campo label="Acento del efecto">
                    <div className="flex items-center gap-2">
                      <input type="color" value={edit.acento_override ?? EFECTO_ACENTO[edit.efecto ?? ''] ?? '#c6303a'} onChange={(e) => setEdit({ ...edit, acento_override: e.target.value })} className="w-9 h-9 rounded border" />
                      <span className="text-xs text-muted-foreground">{edit.acento_override ? 'personalizado' : 'por defecto del efecto'}</span>
                      {edit.acento_override && <button className="text-xs text-red-500" onClick={() => setEdit({ ...edit, acento_override: null })}>restablecer</button>}
                    </div>
                  </Campo>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">Intensidad</span>
                  {[1, 2, 3].map((n) => <button key={n} className={`w-8 h-8 rounded-md border ${(edit.intensidad ?? 2) === n ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`} onClick={() => setEdit({ ...edit, intensidad: n })}>{n}</button>)}
                  <div className="ml-auto"><Switch checked={edit.activa ?? true} onChange={(v) => setEdit({ ...edit, activa: v })} label="Activa" /></div>
                </div>
                <div className="flex justify-end gap-2 pt-1"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Vista previa del efecto</div>
                <div className="aspect-video rounded-lg overflow-hidden border">
                  <SignagePlayer data={previewData} campana={edit.efecto ? { efecto: edit.efecto, intensidad: edit.intensidad ?? 2, acento: edit.acento_override, saludo: edit.saludo } : null} />
                </div>
              </div>
            </div>
        </PanelIntegrado>
      )}
    </div>
  );
}

function edLabelLocal(edit: Partial<Campana> | null): string { return edit?.nombre || 'Campaña'; }
